import React, { useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext, IntlContext } from "context";
import { Role } from "types/user";
import { Button } from "components/ui/button";
import { Heart } from "lucide-react";
import { profileKeys, toggleProviderFav } from "hooks/profile";
import { serviceKeys } from "hooks/service";
import { cn } from "lib/utils";

interface Props {
    providerId: string;
    clientsFav?: string[];
    className?: string;
    size?: "default" | "sm";
}

const FavoriteProviderButton: React.FC<Props> = ({
    providerId,
    clientsFav = [],
    className,
    size = "default",
}) => {
    const intl = useContext(IntlContext);
    const { account, currentRole, isLoggedIn } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const clientId = account?.resources?.client?.[0]?.id;
    const [isPending, setIsPending] = useState(false);
    const [isFavorite, setIsFavorite] = useState(
        () => clientId != null && clientsFav.includes(clientId),
    );

    if (!isLoggedIn || currentRole !== Role.CLIENT || !clientId) {
        return null;
    }

    const title = isFavorite
        ? intl.formatMessage("provider.favorite.remove_title")
        : intl.formatMessage("provider.favorite.add_title");

    const handleToggle = async (event?: React.MouseEvent) => {
        event?.stopPropagation();
        event?.preventDefault();

        if (isPending) {
            return;
        }

        setIsPending(true);
        try {
            const result = await toggleProviderFav(providerId);
            setIsFavorite(result.is_favorite);
            await queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
            await queryClient.invalidateQueries({ queryKey: serviceKeys.ourDetails() });
            await queryClient.invalidateQueries({ queryKey: profileKeys.providerFavorites() });
        } catch {
            // no-op
        } finally {
            setIsPending(false);
        }
    };

    const iconClass = size === "sm" ? "h-4 w-4" : "h-5 w-5";
    const buttonClass =
        size === "sm"
            ? "h-9 w-9 rounded-xl"
            : "w-12 h-12 rounded-[14px] bg-slate-50 border border-[#f1f5f9] hover:bg-[#f1f5f9]";

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
                "flex items-center justify-center transition",
                buttonClass,
                isFavorite ? "text-red-500" : "text-[#5a607c]",
                className,
            )}
            title={title}
            aria-label={title}
            aria-pressed={isFavorite}
            disabled={isPending}
            onClick={(event) => void handleToggle(event)}
        >
            <Heart aria-hidden="true" className={cn(iconClass, isFavorite && "fill-current")} />
        </Button>
    );
};

export default FavoriteProviderButton;
